/**
 * A curved canal crossing (lifted from lantern-water's `world.js` `bridge`):
 * one height profile drives the deck, the arch ribs, the sills and copings
 * (stone) or the ribs, rails, posts and X-braces (timber), joining unequal
 * banks with a rising crown. Authored with the root at the span's centre
 * (the prototype's x 7.15 and the bridge's z): the span runs along local x
 * from -3.8 to 3.8 (the prototype's 3.35 to 10.95) and is 3.5 wide.
 *
 * Params: `left` and `right` (0): the deck's height at the west and east
 * ends; `rise` (1.05): the crown above the straight line between them;
 * `timber` (false): a timber span instead of stone. The walking surface
 * (the same profile), the rail colliders and the water's cheap reflection
 * of the span are the scene record's.
 */
import * as THREE from 'three';
import { bridgeBalustrade } from '../bridge-balustrade.ts';
import { flag, num } from '../kit.ts';
import type { Prefab } from './types.ts';

const start = -3.8;
const end = 3.8;
const count = 28;

export const prefab: Prefab = {
  id: 'bridge',
  buildPrefab(ctx) {
    const root = new THREE.Group();
    const { box, line, m } = ctx;
    const left = num(ctx, 'left', 0);
    const right = num(ctx, 'right', 0);
    const rise = num(ctx, 'rise', 1.05);
    const timber = flag(ctx, 'timber', false);
    const z = 0;
    const profile = (x: number): number => {
      const t = THREE.MathUtils.clamp((x - start) / (end - start), 0, 1);
      return THREE.MathUtils.lerp(left, right, t) + 0.06 + Math.sin(t * Math.PI) * rise;
    };
    for (let i = 0; i < count; i += 1) {
      const x0 = THREE.MathUtils.lerp(start, end, i / count);
      const x1 = THREE.MathUtils.lerp(start, end, (i + 1) / count);
      const y0 = profile(x0);
      const y1 = profile(x1);
      const slope = Math.atan2(y1 - y0, x1 - x0);
      const length = Math.hypot(x1 - x0, y1 - y0) + 0.014;
      const x = (x0 + x1) / 2;
      const y = (y0 + y1) / 2;
      const deck = box(root, x, y - 0.15, z, timber ? length - 0.014 : length, 0.3, 3.5, timber ? m.wood : m.paving);
      deck.rotation.z = slope;
      for (const side of [-1, 1]) {
        const zz = z + side * 1.72;
        if (timber) {
          box(root, x, y - 0.36, zz, length, 0.39, 0.22, m.darkWood).rotation.z = slope;
          box(root, x, y + 0.97, zz, length, 0.1, 0.16, m.wood).rotation.z = slope;
          box(root, x, y + 0.22, zz, length, 0.09, 0.12, m.darkWood).rotation.z = slope;
          if (i % 4 === 0 || i === count - 1) {
            box(root, x, y + 0.6, zz, 0.15, 1.23, 0.18, m.wood);
            box(root, x, y + 1.18, zz, 0.2, 0.065, 0.22, m.iron);
            box(root, x, y + 0.28, zz, 0.16, 0.085, 0.19, m.iron);
            if (i + 4 < count) {
              const endX = x + ((end - start) * 4) / count;
              const endY = profile(endX);
              line(root, [x, y + 0.28, zz], [endX, endY + 0.92, zz], 0.055, m.darkWood);
              line(root, [x, y + 0.92, zz], [endX, endY + 0.28, zz], 0.055, m.darkWood);
            }
          }
          continue;
        }
        // A continuous carved arch ring follows the deck, leaving a visible opening.
        box(root, x, y - 0.43, zz, length - 0.006, 0.68, 0.4, m.stone).rotation.z = slope;
        box(root, x, y + 0.12, zz, length, 0.24, 0.38, m.stone).rotation.z = slope;
        box(root, x, y + 0.98, zz, length, 0.13, 0.43, m.cream).rotation.z = slope;
      }
    }
    if (!timber) bridgeBalustrade(ctx, root, start, end, z, profile);
    for (const side of [-1, 1]) {
      for (const x of [start + 0.2, end - 0.2]) {
        const top = profile(x) - 0.25;
        box(root, x, (top - 1.45) / 2, z + side * 1.64, 0.6, top + 1.45, 0.55, timber ? m.darkWood : m.stone);
      }
    }
    return { root };
  },
};
